import React, {useState, useEffect} from "react";
import {sweetAlertQuestion, sweetAlerts} from "../../utils/sweet-alerts";
import {getAllSubmittedForms, getFormDetails, updateFormStatus, getUserList, deleteFeedback} from "../../services/adminServices";
import CustomLoader from "../../CustomComponents/CustomLoader";
import moment from "moment";
import {Autocomplete, TextField, CircularProgress} from "@mui/material";
import CustomUserName from "../../CustomComponents/CustomUserName";

const Feedback = () => {
	const [firstTimeLoad, setFirstTimeLoad] = useState(true);
	const [forms, setForms] = useState([]);
	const [filteredForms, setFilteredForms] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedForm, setSelectedForm] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [filterStatus, setFilterStatus] = useState("all");
	const [sortBy, setSortBy] = useState("date-desc");
	const [page, setPage] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [totalPages, setTotalPages] = useState(1);
	const [totalLength, setTotalLength] = useState(0);
	const [loadingButtons, setLoadingButtons] = useState({});

	// Fancybox functionality for multiple file types
	const [fancyboxFile, setFancyboxFile] = useState(null);
	const [showFancybox, setShowFancybox] = useState(false);
	const [fileContent, setFileContent] = useState(null);
	const [loadingFile, setLoadingFile] = useState(false);

	// User search functionality
	const [open, setOpen] = useState(false);
	const [options, setOptions] = useState([]);
	const [loadingUser, setLoadingUser] = useState(true);
	const [debounceTimer, setDebounceTimer] = useState(null);
	const [selectedUserId, setSelectedUserId] = useState(null);
	const API_URL = process.env.REACT_APP_API_URL;

	const [inputValue, setInputValue] = useState("");

	const formTypes = [
		{value: "all", label: "All Forms"},
		{value: "bug-report", label: "Bug Report"},
		{value: "feature-request", label: "Feature Request"},
		{value: "general-feedback", label: "General Feedback"},
		{value: "account-issue", label: "Account Issue"},
		{value: "question", label: "Question"},
		{value: "compliment", label: "Compliment"},
	];

	const statusOptions = [
		{value: "all", label: "All Status"},
		{value: "pending", label: "Pending"},
		{value: "reviewed", label: "Reviewed"},
		{value: "resolved", label: "Resolved"},
		{value: "closed", label: "Closed"},
	];

	const getFormTypeIcon = (type) => {
		const icons = {
			"bug-report": "🐛",
			"feature-request": "💡",
			"general-feedback": "💬",
			"account-issue": "👤",
			question: "❓",
			compliment: "👏",
		};
		return icons[type] || "📄";
	};

	const getFormTypeColor = (type) => {
		const colors = {
			"bug-report": "danger",
			"feature-request": "primary",
			"general-feedback": "info",
			"account-issue": "warning",
			question: "secondary",
			compliment: "success",
		};
		return colors[type] || "secondary";
	};

	const getStatusBadge = (status) => {
		const statusConfig = {
			pending: {class: "badge-warning", text: "Pending"},
			reviewed: {class: "badge-info", text: "Reviewed"},
			resolved: {class: "badge-success", text: "Resolved"},
			closed: {class: "badge-danger", text: "Closed"},
		};

		const config = statusConfig[status] || {
			class: "badge-secondary",
			text: status,
		};
		return <span className={`badge ${config.class}`}>{config.text}</span>;
	};

	const handleSearch = async (query) => {
		if (!query || query.length < 3) {
			setOptions([]);
			return;
		}
		setLoadingUser(true);

		const timer = setTimeout(async () => {
			try {
				const res = await getUserList({search: query});
				setOptions(res?.data);
			} catch (error) {
				console.error("API error:", error);
			} finally {
				setLoadingUser(false);
			}
		}, 300);

		setDebounceTimer(timer);
	};

	const fetchForms = async () => {
		if (firstTimeLoad) {
			setLoading(false);
		}
		try {
			const params = {
				page: page,
				pageSize: pageSize,
				status: filterStatus,
				sortBy: sortBy,
				userId: selectedUserId,
			};

			const response = await getAllSubmittedForms(params);

			if (response && response.status === true) {
				const transformedForms =
					response?.data?.list?.map((form) => ({
						...form,
						userName: form.user ? `${form.user.first_name} ${form.user.last_name}` : "N/A",
						submittedAt: form.createdAt,
						preview: form.message || form.subject || "No preview available",
					})) || [];

				setTotalCount(response?.data?.totalCount);
				setLoading(true);
				setForms(transformedForms);
				setTotalLength(response?.data?.totalCount || 0);
				const totalPages = Math.ceil(response?.data?.totalCount / pageSize);
				setTotalPages(totalPages);
			} else {
				setLoading(true);
				setForms([]);
			}
		} catch (error) {
			console.error("Error fetching forms:", error);
			setForms([]);
		}
	};

	const handleViewDetails = async (form) => {
		try {
			setSelectedForm(form);
			setShowModal(true);
		} catch (error) {
			console.error("Error fetching form details:", error);
			sweetAlerts("error", "Failed to fetch form details");
		}
	};

	const handleStatusUpdate = async (formId, formType, newStatus) => {
		const confirmed = await sweetAlertQuestion("Update Status", `Are you sure you want to change the status to ${newStatus}?`);

		if (!confirmed) return;

		try {
			const response = await updateFormStatus(formId, formType, newStatus);
			if (response && response.status === true) {
				sweetAlerts("success", "Status updated successfully");
				fetchForms();
			} else {
				sweetAlerts("error", "Failed to update status");
			}
		} catch (error) {
			console.error("Error updating status:", error);
			sweetAlerts("error", "Failed to update status");
		}
	};

	const paginate = (pageNumber) => setPage(pageNumber);

	const truncateText = (text, maxLength = 50) => {
		if (!text) return "N/A";
		return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
	};

	const formatDate = (date) => {
		return moment(date).format("MMM DD, YYYY");
	};

	useEffect(() => {
		fetchForms();
	}, [pageSize, page]);

	// Update forms when forms changes
	useEffect(() => {
		setForms(forms);
		setFilteredForms(forms);
		setLoadingButtons({});
	}, [forms]);

	// Filter and sort forms
	useEffect(() => {
		if (firstTimeLoad) {
			setFirstTimeLoad(false);
		} else {
			setPage(1);
			fetchForms();
		}
	}, [filterStatus, sortBy, selectedUserId]);

	// File type detection and fancybox functions
	const getFileType = (url) => {
		const extension = url.split(".").pop().toLowerCase();
		if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(extension)) {
			return "image";
		} else if (extension === "pdf") {
			return "pdf";
		} else if (["txt", "log", "md", "json", "xml", "csv"].includes(extension)) {
			return "text";
		}
		return "unknown";
	};

	const getFileIcon = (fileType) => {
		switch (fileType) {
			case "image":
				return "🖼️";
			case "pdf":
				return "📄";
			case "text":
				return "📝";
			default:
				return "📁";
		}
	};

	const openFancybox = async (fileUrl) => {
		const fullUrl = `${API_URL}${fileUrl}`;
		const fileType = getFileType(fileUrl);

		setFancyboxFile({url: fullUrl, type: fileType, originalUrl: fileUrl});
		setShowFancybox(true);
		setLoadingFile(true);
		setFileContent(null);

		// Load text file content if it's a text file
		if (fileType === "text") {
			try {
				const response = await fetch(fullUrl);
				const text = await response.text();
				setFileContent(text);
			} catch (error) {
				console.error("Error loading text file:", error);
				setFileContent("Error loading file content");
			}
		}

		setLoadingFile(false);
	};

	const closeFancybox = () => {
		setShowFancybox(false);
		setFancyboxFile(null);
		setFileContent(null);
		setLoadingFile(false);
	};

	const handleDeleteComment = async (feedbackId) => {
		sweetAlertQuestion("Are you sure you want to delete this? This action cannot be undone.").then(async (result) => {
			if (result === "Yes") {
				setLoadingButtons((prev) => ({...prev, [`delete-${feedbackId}`]: true}));
				try {
					// In real app, this would be an API call like: const response = await deleteComment(commentId);

					// Simulate API call
					await new Promise((resolve) => setTimeout(resolve, 500));

					const response = await deleteFeedback(feedbackId);
					if (response && response.status === true) {
						if (filteredForms.length === 1) {
							setPage(page - 1);
						}
						fetchForms();
						sweetAlerts("success", "Feedback deleted successfully");
					} else {
						sweetAlerts("error", "Failed deleting rating. Please try again.");
					}
				} catch (error) {
					console.error("Error deleting feedback:", error);
					sweetAlerts("error", "Failed deleting feedback. Please try again.");
				} finally {
					setLoadingButtons((prev) => ({...prev, [`delete-${feedbackId}`]: false}));
				}
			}
		});
	};

	// Handle keyboard events for fancybox
	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === "Escape" && showFancybox) {
				closeFancybox();
			}
		};

		if (showFancybox) {
			document.addEventListener("keydown", handleKeyDown);
			document.body.style.overflow = "hidden";
		}

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = "unset";
		};
	}, [showFancybox]);

	const renderFancyboxContent = () => {
		if (!fancyboxFile) return null;

		const {url, type} = fancyboxFile;

		switch (type) {
			case "image":
				return <img src={url} alt="Attachment" className="fancybox-image" />;

			case "pdf":
				return <iframe src={url} className="fancybox-pdf" title="PDF Document" />;

			case "text":
				return <div className="fancybox-text">{loadingFile ? "Loading..." : fileContent || "No content available"}</div>;

			default:
				return (
					<div className="fancybox-text">
						<p>
							Unsupported file type.{" "}
							<a href={url} target="_blank" rel="noopener noreferrer">
								Click here to download
							</a>
						</p>
					</div>
				);
		}
	};

	return (
		<div className="admin-ratings-dashboard-page">
			<div className="page-header">
				<h2>User Feedback</h2>
				<p>View and manage all user feedback submissions ({totalLength} total)</p>
			</div>

			{/* Filters and Search */}
			<div className="ratings-controls">
				<div className="filter-section">
					<select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
						<option value="all">All Status</option>
						<option value="pending">Pending</option>
						<option value="reviewed">Reviewed</option>
						<option value="resolved">Resolved</option>
						<option value="closed">Closed</option>
					</select>

					<select className="filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
						<option value="date-desc">Newest First</option>
						<option value="date-asc">Oldest First</option>
					</select>

					<Autocomplete
						open={open}
						onOpen={() => setOpen(true)}
						onClose={() => setOpen(false)}
						options={options}
						getOptionLabel={(option) => option?.first_name + " " + option?.last_name || ""}
						loading={loadingUser}
						inputValue={inputValue}
						onInputChange={(e, value, reason) => {
							setInputValue(value);
							handleSearch(value);

							// Clear selection when user types or backspaces
							if (reason === "input") {
								const selectedUser = selectedUserId ? options.find((opt) => opt.user_id === selectedUserId) : null;
								const selectedUserName = selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : "";

								// If the current input doesn't match the selected user's name, clear selection
								if (value !== selectedUserName) {
									setSelectedUserId(null);
								}
							}
						}}
						onChange={(e, value) => {
							if (value) {
								setSelectedUserId(value.user_id);
								setInputValue(value.first_name + " " + value.last_name);
							} else {
								setSelectedUserId(null);
								setInputValue("");
							}
						}}
						value={selectedUserId ? options.find((opt) => opt.user_id === selectedUserId) || null : null}
						clearIcon={<i className="fa-2xs fas fa-close" />}
						popupIcon={null}
						autoComplete
						includeInputInList
						filterSelectedOptions
						renderInput={(params) => (
							<TextField
								{...params}
								placeholder="User Name"
								sx={{
									"& .MuiOutlinedInput-root": {
										borderRadius: "10px",
										backgroundColor: "#fff",
										"& .MuiOutlinedInput-input": {
											padding: "10px 2px",
											height: 3,
										},
										"& fieldset": {
											borderColor: "#e9ecef",
											borderWidth: "2px",
										},
										"&:hover fieldset": {
											borderColor: "#e9ecef",
											borderWidth: "2px",
										},
										"&.Mui-focused fieldset": {
											borderColor: "#28a745",
											borderWidth: "2px",
											boxShadow: "0 0 0 .2rem #22c55e40",
										},
									},
								}}
								InputProps={{
									...params.InputProps,
									endAdornment: <>{params.InputProps.endAdornment}</>,
								}}
							/>
						)}
						sx={{width: 250}}
					/>
				</div>
			</div>

			{/* Forms List */}
			{loading ? (
				<div className="admin-ratings-container">
					{filteredForms.length === 0 ? (
						<div className="ratings-table-container">
							<div className="table-responsive sticky-table">
								<table className="ratings-table">
									<thead>
										<tr>
											<th className="align-middle address-cell">Type</th>
											<th className="align-middle address-cell">User Name</th>
											<th className="align-middle address-cell">Subject</th>
											<th className="align-middle address-cell">Message Preview</th>
											<th className="align-middle address-cell">Status</th>
											<th className="align-middle address-cell">Submitted</th>
											<th className="actions-cell text-end">Actions</th>
										</tr>
									</thead>
									<tbody>
										<tr className="rating-row">
											<td className="address-cell text-start" colSpan={7}>
												No feedback found
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					) : (
						<div className="ratings-table-container">
							<div className="table-responsive sticky-table">
								<table className="ratings-table">
									<thead>
										<tr>
											<th className="align-middle address-cell">Type</th>
											<th className="align-middle address-cell">User Name</th>
											<th className="align-middle address-cell">Subject</th>
											<th className="align-middle address-cell">Message Preview</th>
											<th className="align-middle address-cell">Status</th>
											<th className="align-middle address-cell">Submitted</th>
											<th className="actions-cell text-end">Actions</th>
										</tr>
									</thead>
									<tbody>
										{filteredForms.map((form) => (
											<tr key={form.feedback_id} className="comment-row" data-id={form.feedback_id}>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(form)}>
													<span className={`badge badge-${getFormTypeColor(form.feedback_type)}`}>
														{getFormTypeIcon(form.feedback_type)} {form.feedback_type}
													</span>
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(form)} title={form.userName}>
													<CustomUserName user={form.user} />{" "}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(form)} title={form.subject}>
													{truncateText(form.subject, 40)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(form)} title={form.message}>
													{truncateText(form.message, 50)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(form)}>
													{getStatusBadge(form.status)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(form)}>
													{formatDate(form.submittedAt)}
												</td>
												<td className="align-middle actions-cell">
													<div className="table-actions mb-0 justify-content-end flex-row">
														<button className="btn-icon" onClick={() => handleViewDetails(form)} title="View Details">
															<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																<path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" />
																<circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
															</svg>
														</button>
														<div className="status-dropdown">
															<select value={form.status} onChange={(e) => handleStatusUpdate(form.feedback_id, form.type, e.target.value)} className="filter-select">
																<option value="pending">Pending</option>
																<option value="reviewed">Reviewed</option>
																<option value="resolved">Resolved</option>
																<option value="closed">Closed</option>
															</select>
														</div>
														<button className="btn-icon btn-delete" onClick={() => handleDeleteComment(form.feedback_id)} title="Delete Feedback" disabled={loadingButtons[`delete-${form.feedback_id}`]}>
															{loadingButtons[`delete-${form.feedback_id}`] ? (
																<span className="spinner-small"></span>
															) : (
																<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																	<path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
																	<path
																		d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																</svg>
															)}
														</button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							{/* Pagination */}
							<>
								<div className="pagination justify-content-between mt-0 pt-0 pe-3 px-3">
									<div className="pagination-info d-flex justify-content-between align-items-center gap-3">
										<span className="pagination-info-text">
											Showing {page * pageSize - pageSize + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
										</span>
										<div>
											<select className="filter-select" value={pageSize} onChange={(e) => setPageSize(e.target.value)}>
												<option value="10">10</option>
												<option value="20">20</option>
												<option value="25">25</option>
												<option value="50">50</option>
												<option value="100">100</option>
											</select>
										</div>
									</div>
									<div className="d-flex justify-content-end pb-3 pt-3 pe-2">
										<button className="pagination-btn" onClick={() => paginate(page - 1)} disabled={page === 1}>
											Previous
										</button>

										<div className="pagination-numbers">
											{[...Array(totalPages)].map((_, index) => {
												const ipage = index + 1;
												if (ipage === 1 || ipage === totalPages || (ipage >= page - 1 && ipage <= page + 1)) {
													return (
														<button key={ipage} className={`pagination-number ${page === ipage ? "active" : ""}`} onClick={() => paginate(ipage)}>
															{ipage}
														</button>
													);
												} else if (ipage === page - 2 || ipage === page + 2) {
													return (
														<span key={ipage} className="pagination-ellipsis">
															...
														</span>
													);
												}
												return null;
											})}
										</div>

										<button className="pagination-btn" onClick={() => paginate(page + 1)} disabled={page === totalPages}>
											Next
										</button>
									</div>
								</div>
							</>
						</div>
					)}
				</div>
			) : (
				<div className="admin-ratings-container">
					<div className="ratings-table-container">
						<div className="table-responsive sticky-table">
							<table className="ratings-table">
								<thead>
									<tr>
										<th className="align-middle address-cell">Type</th>
										<th className="align-middle address-cell">User Name</th>
										<th className="align-middle address-cell">Subject</th>
										<th className="align-middle address-cell">Message Preview</th>
										<th className="align-middle address-cell">Status</th>
										{/* <th className="align-middle address-cell">Submitted</th> */}
										<th className="actions-cell text-end">Actions</th>
									</tr>
								</thead>
								<tbody>
									<tr className="rating-row">
										<td className="address-cell text-center" colSpan={6}>
											<CustomLoader />
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			)}

			{/* Detail Modal */}
			{showModal && selectedForm && (
				<div className="modal-overlay" onClick={() => setShowModal(false)}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<div className="modal-header">
							<h3>Feedback Details</h3>
							<button className="modal-close" onClick={() => setShowModal(false)}>
								×
							</button>
						</div>

						<div className="modal-body">
							<div className="detail-section">
								<h4>Feedback Information</h4>
								<div className="detail-grid">
									<div className="detail-item">
										<span className="detail-label">Type:</span>
										<span className="detail-value">
											<span className={`badge badge-${getFormTypeColor(selectedForm.feedback_type)}`}>
												{getFormTypeIcon(selectedForm.feedback_type)} {selectedForm.feedback_type}
											</span>
										</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Status:</span>
										<span className="detail-value">{getStatusBadge(selectedForm.status)}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">User:</span>
										<span className="detail-value">{selectedForm.userName || "N/A"}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Email:</span>
										<span className="detail-value">{selectedForm.email || "N/A"}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Phone:</span>
										<span className="detail-value">{selectedForm.phone || "N/A"}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Submitted:</span>
										<span className="detail-value">{formatDate(selectedForm.submittedAt)}</span>
									</div>
								</div>
							</div>

							<div className="detail-section">
								<h4>Subject</h4>
								<div className="job-description-full">
									<p>{selectedForm.subject}</p>
								</div>
							</div>

							<div className="detail-section">
								<h4>Message</h4>
								<div className="job-description-full">
									<p>{selectedForm.message}</p>
								</div>
							</div>

							{selectedForm.file_1_url && (
								<div className="detail-section">
									<h4>Attachments</h4>
									<div className="attachment-grid d-flex gap-2 flex-wrap justify-content-between">
										{selectedForm.file_1_url && (
											<div className="attachment-item w-45" onClick={() => openFancybox(selectedForm.file_1_url)}>
												{getFileType(selectedForm.file_1_url) === "image" ? (
													<img src={`${API_URL}${selectedForm.file_1_url}`} alt="Attachment 1" />
												) : (
													<div className="file-icon">{getFileIcon(getFileType(selectedForm.file_1_url))}</div>
												)}
												<div className="file-type-badge">{getFileType(selectedForm.file_1_url).toUpperCase()}</div>
											</div>
										)}
										{selectedForm.file_2_url && (
											<div className="attachment-item w-45" onClick={() => openFancybox(selectedForm.file_2_url)}>
												{getFileType(selectedForm.file_2_url) === "image" ? (
													<img src={`${API_URL}${selectedForm.file_2_url}`} alt="Attachment 2" />
												) : (
													<div className="file-icon">{getFileIcon(getFileType(selectedForm.file_2_url))}</div>
												)}
												<div className="file-type-badge">{getFileType(selectedForm.file_2_url).toUpperCase()}</div>
											</div>
										)}
										{selectedForm.file_3_url && (
											<div className="attachment-item w-45" onClick={() => openFancybox(selectedForm.file_3_url)}>
												{getFileType(selectedForm.file_3_url) === "image" ? (
													<img src={`${API_URL}${selectedForm.file_3_url}`} alt="Attachment 3" />
												) : (
													<div className="file-icon">{getFileIcon(getFileType(selectedForm.file_3_url))}</div>
												)}
												<div className="file-type-badge">{getFileType(selectedForm.file_3_url).toUpperCase()}</div>
											</div>
										)}
									</div>
								</div>
							)}
						</div>

						<div className="modal-footer">
							<button className="btn btn-secondary" onClick={() => setShowModal(false)}>
								Close
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Multi-format Fancybox Modal */}
			{showFancybox && fancyboxFile && (
				<div className="fancybox-overlay" onClick={closeFancybox}>
					<div className="fancybox-content" onClick={(e) => e.stopPropagation()}>
						<button className="fancybox-close" onClick={closeFancybox}>
							×
						</button>
						{renderFancyboxContent()}
					</div>
				</div>
			)}
		</div>
	);
};

export default Feedback;
